// ===========================================
// Invoice status chip — with the send-failure warning
// ===========================================
// A DRAFT invoice whose last send delivered to nobody carries the reason on the
// row (`lastSendFailureReason`, written by the backend on failure and cleared
// on success). Before 2026-09-09 that reason lived only in the server log, so
// an admin saw "DRAFT" and had no way to know the send had been tried at all.

import { Box, Chip, Tooltip } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type { Invoice } from '../../api/invoices.api';
import { INVOICE_STATUS_COLORS, invoiceStatusLabel } from '../../utils/invoiceDisplay';

export type InvoiceStatusChipInvoice = Pick<
  Invoice,
  'status' | 'lastSendFailedAt' | 'lastSendFailureReason'
>;

/**
 * The tooltip text for a failed send, or null when there is nothing to warn
 * about. Only a DRAFT carries the warning: a successful send clears the
 * reason, and a cancelled invoice's old failure is history, not a call to act.
 *
 * `lastSendFailedAt` is a genuine instant (not a `@db.Date`), so it renders in
 * the viewer's local time — the same treatment `sentAt` gets on the page.
 */
export function describeSendFailure(invoice: InvoiceStatusChipInvoice): string | null {
  if (invoice.status !== 'DRAFT' || !invoice.lastSendFailureReason) return null;
  const when = invoice.lastSendFailedAt
    ? new Intl.DateTimeFormat('en-AU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(invoice.lastSendFailedAt))
    : null;
  return when
    ? `Last send failed ${when}: ${invoice.lastSendFailureReason}`
    : `Last send failed: ${invoice.lastSendFailureReason}`;
}

export function InvoiceStatusChip({ invoice }: { invoice: InvoiceStatusChipInvoice }) {
  const failure = describeSendFailure(invoice);
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <Chip
        label={invoiceStatusLabel(invoice.status)}
        size="small"
        sx={{
          backgroundColor: INVOICE_STATUS_COLORS[invoice.status].bg,
          color: INVOICE_STATUS_COLORS[invoice.status].text,
          fontWeight: 600,
          fontSize: '0.75rem',
        }}
      />
      {failure && (
        <Tooltip title={failure}>
          <WarningAmberIcon
            color="warning"
            fontSize="small"
            role="img"
            aria-label="Send failed"
            data-testid="invoice-send-failed"
          />
        </Tooltip>
      )}
    </Box>
  );
}
